import { useEffect, useRef, useState } from 'react';
import type { PlayerBindings } from '../input/bindings';
import type { KeyboardController } from '../input/keyboard';
import type { PadKey } from './types';

type Dir = 'forward' | 'back' | 'left' | 'right';
const DIR_KEY: Record<Dir, PadKey> = { forward: 'up', back: 'down', left: 'left', right: 'right' };

/**
 * On-screen joystick for phones and tablets. Drives Player 1's keys through the
 * keyboard controller, so every keyboard game works by touch with no game changes.
 * Only the directions the game actually uses are sent.
 */
export function TouchStick({ kb, keys, used }: { kb: KeyboardController; keys: PlayerBindings; used: Partial<Record<PadKey, string>> }) {
  const ref = useRef<HTMLDivElement>(null);
  const held = useRef(new Set<Dir>());
  const [knob, setKnob] = useState<{ x: number; y: number } | null>(null);

  const apply = (next: Set<Dir>) => {
    for (const d of held.current) if (!next.has(d)) kb.virtualUp(keys[d]);
    for (const d of next) if (!held.current.has(d)) kb.virtualDown(keys[d]);
    held.current = next;
  };
  const release = () => {
    apply(new Set());
    setKnob(null);
  };
  useEffect(() => () => release(), []); // eslint-disable-line react-hooks/exhaustive-deps

  const track = (e: React.PointerEvent) => {
    const r = ref.current!.getBoundingClientRect();
    const rad = r.width / 2;
    let dx = (e.clientX - (r.left + rad)) / rad;
    let dy = (e.clientY - (r.top + rad)) / rad;
    const len = Math.hypot(dx, dy);
    if (len > 1) {
      dx /= len;
      dy /= len;
    }
    setKnob({ x: dx, y: dy });
    const next = new Set<Dir>();
    const dead = 0.32;
    if (dy < -dead && used.up) next.add('forward');
    if (dy > dead && used.down) next.add('back');
    if (dx < -dead && used.left) next.add('left');
    if (dx > dead && used.right) next.add('right');
    apply(next);
  };

  return (
    <div
      ref={ref}
      className={`touch-stick${knob ? ' is-active' : ''}`}
      role="application"
      aria-label="Movement joystick"
      onPointerDown={(e) => {
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        track(e);
      }}
      onPointerMove={(e) => knob && track(e)}
      onPointerUp={release}
      onPointerCancel={release}
      onLostPointerCapture={release}
    >
      <span className="touch-stick__knob" style={knob ? { transform: `translate(${knob.x * 60}%, ${knob.y * 60}%)` } : undefined} />
    </div>
  );
}

/** Big action button (jump / shoot / swing…), labelled with what the game says it does. */
export function TouchAction({ kb, code, label }: { kb: KeyboardController; code: string; label: string }) {
  const [down, setDown] = useState(false);
  const up = () => {
    setDown(false);
    kb.virtualUp(code);
  };
  useEffect(() => () => kb.virtualUp(code), [kb, code]);
  return (
    <button
      type="button"
      className={`touch-fire touch-fire--p1${down ? ' is-active' : ''}`}
      onPointerDown={(e) => {
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        setDown(true);
        kb.virtualDown(code);
      }}
      onPointerUp={up}
      onPointerCancel={up}
      onLostPointerCapture={up}
      onContextMenu={(e) => e.preventDefault()}
    >
      {label}
    </button>
  );
}

export const usedDirs = (keys: Partial<Record<PadKey, string>>) => (Object.keys(DIR_KEY) as Dir[]).some((d) => keys[DIR_KEY[d]]);
